import classNames from 'classnames';
import { Icon } from 'components/Icon';
import { TableCell } from 'components/Table/TableCell';
import { TableRow } from 'components/Table/TableRow';
import { PortalMemberName } from 'features/PortalMembers/PortalMemberName';
import { usePermission } from 'hooks/usePermission';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInDays } from 'utils/dateHandling.utils';
import {
  APIKeyData,
  APIKeyLastAction,
  APIKeyStatus,
  Permission,
} from '../../../utils/types';

type APIKeyTableRowProps = {
  apiDetails: APIKeyData;
  showUserName: boolean;
  handleShowAPIDeleteKeyModal: () => void;
  key: string;
};

export const APIKeyTableRow: React.FC<APIKeyTableRowProps> = ({
  apiDetails,
  showUserName,
  handleShowAPIDeleteKeyModal,
}: APIKeyTableRowProps) => {
  const { t } = useTranslation();
  const currentDate = new Date().getTime();
  const expiringDate = new Date(apiDetails.dateExpiry).getTime();
  const daysRemaining = differenceInDays(expiringDate, currentDate);
  const isExpiring = daysRemaining <= 30 ? true : false;
  const { userHasPermission } = usePermission();

  const canDeleteKey =
    apiDetails.status === APIKeyStatus.ACTIVE &&
    userHasPermission(Permission.DATA_CONNECTION_DELETE_KEYS);

  return (
    <TableRow hoverable>
      {showUserName && (
        <TableCell>
          <PortalMemberName
            name={apiDetails.userDetails?.name}
            email={apiDetails.userDetails?.email?.toLowerCase()}
          />
        </TableCell>
      )}
      <TableCell>
        <div>{apiDetails.projectName}</div>
        {apiDetails.lastAction === APIKeyLastAction.MOVED && (
          <div>(transferred)</div>
        )}
      </TableCell>
      <TableCell>{apiDetails.scopes.join(', ')}</TableCell>
      <TableCell>
        {new Date(apiDetails.dateCreated).toLocaleString('en-GB', {
          day: '2-digit',
          month: 'numeric',
          year: 'numeric',
        })}
      </TableCell>
      <TableCell>
        {t(
          `features:collaboration:api-management:keyStatus:${apiDetails.status}`,
        )}
      </TableCell>
      <TableCell>
        <div
          className={classNames('flex', {
            'font-semibold text-red-100': isExpiring,
          })}
        >
          {apiDetails.status === 'cancelled'
            ? '-'
            : new Date(apiDetails.dateExpiry).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'numeric',
                year: 'numeric',
              })}
          {!!isExpiring && (
            <span className="w-20px h-20px my-auto ml-1 flex">
              <Icon
                name="InfoCircle"
                color="primary"
                tooltip={
                  daysRemaining >= 0
                    ? t(`features:collaboration:table:daysLeftTooltip`, {
                        daysLeft: daysRemaining,
                      })
                    : t('features:collaboration:table:isExpiredTooltip')
                }
              />
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>{apiDetails.apiKey}</TableCell>
      <TableCell
        justify="end"
        isSticky
        position="last"
        bgColor="bg-white"
        boxShadow
      >
        {canDeleteKey && (
          <button
            title="delete-key"
            className="ml-4 w-4"
            onClick={() => {
              handleShowAPIDeleteKeyModal();
            }}
          >
            <Icon
              name="Delete"
              inline={true}
              color="primary"
              hoverColor="red-100"
            />
          </button>
        )}
      </TableCell>
    </TableRow>
  );
};
